/**
 * API hooks for the Add / Edit Truck Master form.
 *
 * Save goes through POST /api/truck-masters/process?companyId=... which runs the
 * legacy SP_Truck logic on the backend: it generates CNumber / CNumberDisplay for
 * new trucks and decides INSERT vs UPDATE from the id field.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../../api/axios';
import { API_ENDPOINTS } from '../../../api/endpoints';
import type { TruckMasterDto } from '../types/truckMasterForm';
import type { CapacityOptions } from '../utils/truckCapacity';
import { queryKeys } from '../../../lib/queryClient';

const unwrap = <T>(payload: unknown): T => {
  const record = payload as { data?: T } | T;
  if (record && typeof record === 'object' && 'data' in (record as object)) {
    const inner = (record as { data?: T }).data;
    if (inner && typeof inner === 'object') return inner;
  }
  return payload as T;
};

export const truckMasterFormApi = {
  getById: async (id: number): Promise<TruckMasterDto> => {
    const { data } = await api.get(API_ENDPOINTS.TRUCK.GET(id));
    return unwrap<TruckMasterDto>(data);
  },

  /**
   * Sets, moves or clears this truck's default driver.
   *
   * Separate from the truck save on purpose: the pairing is stored on the
   * driver row (DriverMaster.TruckRefId), so saving a truck must never write it.
   * Pass 0 to leave the truck without a driver.
   */
  assignDriver: async (truckId: number, driverRefId: number): Promise<TruckMasterDto> => {
    const { data } = await api.put(API_ENDPOINTS.TRUCK.ASSIGN_DRIVER(truckId), null, {
      params: { driverRefId },
    });
    return unwrap<TruckMasterDto>(data);
  },

  process: async (dto: TruckMasterDto, companyId: number): Promise<TruckMasterDto> => {
    const { data } = await api.post(API_ENDPOINTS.TRUCK.PROCESS, dto, {
      params: { companyId },
    });
    return unwrap<TruckMasterDto>(data);
  },
};

export const useTruckMasterDetail = (id: number | null) =>
  useQuery({
    queryKey: queryKeys.trucks.masterDetail(id),
    queryFn: () => truckMasterFormApi.getById(id as number),
    enabled: Boolean(id && id > 0),
  });

export const useAssignTruckDriver = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ truckId, driverRefId }: { truckId: number; driverRefId: number }) =>
      truckMasterFormApi.assignDriver(truckId, driverRefId),
    onSuccess: (_result, { truckId }) => {
      // Both masters read this pairing, so both caches are stale now.
      queryClient.invalidateQueries({ queryKey: queryKeys.trucks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.drivers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.trucks.masterDetail(truckId) });
    },
  });
};

export const useProcessTruckMaster = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dto, companyId }: { dto: TruckMasterDto; companyId: number }) =>
      truckMasterFormApi.process(dto, companyId),
    onSuccess: (_result, { dto }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trucks.all });
      if (dto.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.trucks.masterDetail(dto.id) });
      }
    },
  });
};

/** Fleet D5a: the size, body type and source lists, served by the backend (one list for web and app). */
export const useTruckCapacityOptions = () =>
  useQuery({
    queryKey: [...queryKeys.trucks.all, 'capacity-options'] as const,
    queryFn: async (): Promise<CapacityOptions> => {
      const { data } = await api.get(API_ENDPOINTS.TRUCK.CAPACITY_OPTIONS);
      const body = data as { Data1?: CapacityOptions; data1?: CapacityOptions; data?: CapacityOptions };
      return body?.Data1 ?? body?.data1 ?? body?.data ?? { sizes: [], bodyTypes: [], sources: [] };
    },
    staleTime: Infinity,
  });
